import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Subscription } from 'rxjs';

import { AutoUnsubscribe, IconNames } from '../../../common';
import { ButtonAction, ButtonSize } from '../../../ui-kit/action-button';
import { AssignableCompartmentList, CompartmentListData, Compartments } from '../models';
import { AssignableCompartmentListFacade } from '../../../store/state/monitoring/assignable-compartments/assignable-compartments.facade';
import { filter } from 'rxjs/operators';
import { FormArray, FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

@Component({
    selector: 'lib-compartments-selection-dialog',
    templateUrl: './compartments-selection-dialog.component.html',
    styleUrls: ['./compartments-selection-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
@AutoUnsubscribe
export class CompartmentsSelectionDialogComponent implements OnInit {
  subscriptions: Subscription[] = [];
  buttonAction = ButtonAction;
  buttonSize = ButtonSize;
  formGroup: FormGroup;
  numberOfDisplayedItems = 10;
  compartmentList: AssignableCompartmentList[];
  hubList: number[];
  iconNames = IconNames;

  constructor(
    public dialogRef: MatDialogRef<CompartmentsSelectionDialogComponent>,
    public assignableCompartmentListFacade: AssignableCompartmentListFacade,
    private fb: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: CompartmentListData,
  ) {
    this.compartmentList = this.data.compartmentList;
    this.hubList = this.data.hubIdList;
    this.assignableCompartmentListFacade.load(this.hubList);
  }

  ngOnInit(): void {
    this.initCodeListForm(this.assignableCompartmentListFacade);
  }

  initCodeListForm(facade: AssignableCompartmentListFacade): void {
    this.subscriptions.push(
      facade.items$.pipe(filter(Boolean)).subscribe((data: AssignableCompartmentList[]) => {
        this.formGroup = this.fb.group({
          dataControl: this.fb.array([]),
        });
        if (this.compartmentList?.length) {
          this.setSelectedListForm(data);
        } else {
          this.setNewListForm(data);
        }
      }),
    );
  }

  confirm(): void {
    const selectedItem: AssignableCompartmentList[] = [];
    this.formGroup.value.dataControl.forEach((item: AssignableCompartmentList) => {
      const compartmentsWithoutSelected = item.compartments
        .filter((compartments: Compartments) => compartments.isSelected)
        .map((compartments: Compartments) => {
          const { isSelected: _isSelected, ...restOfCompartment } = compartments;
          return restOfCompartment;
        });

      if (compartmentsWithoutSelected.length) {
        selectedItem.push({ hubName: item.hubName, compartments: compartmentsWithoutSelected });
      }
    });
    this.dialogRef.close(selectedItem);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private setNewListForm(items: AssignableCompartmentList[]): void {
    const compartmentListArray = this.formGroup.get('dataControl') as FormArray;
    items.forEach((item: AssignableCompartmentList) => {
      const compartmentFormArray = this.fb.array([]);
      item.compartments.forEach((compartment: Compartments) => {
        compartmentFormArray.push(
          new UntypedFormGroup({
            isSelected: new UntypedFormControl(compartment.isSelected ? compartment.isSelected : false),
            name: new UntypedFormControl(compartment.name),
            id: new UntypedFormControl(compartment.id),
          }),
        );
      });
      compartmentListArray.push(
        this.fb.group({
          hubName: [item.hubName],
          compartments: compartmentFormArray,
        }),
      );
    });
  }

  private setSelectedListForm(items: AssignableCompartmentList[]): void {
    const itemWithSelected: AssignableCompartmentList[] = items.map((item: AssignableCompartmentList) => {
      const sameCompartment = this.compartmentList?.find(
        (assignableCompartment: AssignableCompartmentList) => assignableCompartment.hubName === item.hubName,
      );
      if (sameCompartment) {
        const modifiedCompartments = this.addSelectedValue(item.compartments, sameCompartment.compartments);
        return { ...item, compartments: modifiedCompartments };
      }
      return item;
    });
    this.setNewListForm(itemWithSelected);
  }

  private addSelectedValue(compartments: Compartments[], localCompartments: Compartments[]): Compartments[] {
    return compartments.map((itemCompartments: Compartments) => {
      localCompartments.forEach((localCompartment: Compartments) => {
        if (itemCompartments.name === localCompartment.name) {
          itemCompartments = { isSelected: true, ...itemCompartments };
        }
      });
      return itemCompartments;
    });
  }
}
